import { useEffect, useMemo, useRef, useState, forwardRef } from "react";
import { Input } from "@/components/input";
import { Loader } from "@/components/loader";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";

export const InputWithDebounce = forwardRef<
  HTMLInputElement,
  {
    value: string | number;
    onChange: (value: string | number) => void;
    updateLoading?: (loading: boolean) => void;
    withLoader?: boolean;
    withClose?: boolean;
    debounce?: number;
    onLoadingChange?: (loading: boolean) => void;
  } & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">
>(
  (
    {
      value: initialValue,
      onChange,
      updateLoading,
      withLoader = false,
      withClose = false,
      debounce = 500,
      onLoadingChange,
      ...props
    },
    ref
  ) => {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
      onLoadingChange && onLoadingChange(!!loading);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    const debouncedOnChange = useDebounce<string>((val) => {
      onChange(val);
      updateLoading && updateLoading?.(false);
      if (withLoader) {
        setLoading(false);
      }
    }, debounce);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setValue(val);
      updateLoading && updateLoading?.(true);
      if (withLoader) {
        setLoading(true);
      }

      debouncedOnChange(val);
    };

    if (withLoader) {
      return (
        <div className="relative">
          <Input
            ref={ref}
            {...props}
            className={cn("pr-10", props.className)}
            value={value}
            onChange={handleChange}
          />
          <div className="absolute h-full right-0 top-0 flex items-center justify-end pr-2">
            {loading && <Loader height="12px" bars={4} />}
          </div>
        </div>
      );
    }

    return <Input ref={ref} {...props} value={value} onChange={handleChange} />;
  }
);

InputWithDebounce.displayName = "InputWithDebounce";
